import React, { useEffect, useRef, useState, useCallback } from 'react';
import {
  isIOS,
  isMobileDevice,
  shouldUseMediaRecorderFallback,
  supportsWebSpeechRecognition,
} from '../utils/voiceCaptureSupport';
import {
  warmUpMicrophone,
  transcribeAudioBlob,
  createMediaRecorder,
  isTranscriptionUnavailableError,
} from '../utils/mobileAudioCapture';

// Speech Recognition API types
interface SpeechRecognition extends EventTarget {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  start(): void;
  stop(): void;
  abort(): void;
  onstart: (() => void) | null;
  onresult: ((event: SpeechRecognitionEvent) => void) | null;
  onend: (() => void) | null;
  onerror: ((event: SpeechRecognitionErrorEvent) => void) | null;
}

interface SpeechRecognitionEvent {
  resultIndex: number;
  results: SpeechRecognitionResultList;
}

interface SpeechRecognitionResultList {
  length: number;
  item(index: number): SpeechRecognitionResult;
  [index: number]: SpeechRecognitionResult;
}

interface SpeechRecognitionResult {
  length: number;
  item(index: number): SpeechRecognitionAlternative;
  [index: number]: SpeechRecognitionAlternative;
  isFinal: boolean;
}

interface SpeechRecognitionAlternative {
  transcript: string;
  confidence: number;
}

interface SpeechRecognitionErrorEvent {
  error: string;
  message: string;
}

interface GlobalVoiceInputHandlerProps {
  onTranscription: (transcript: string) => void;
  onProcessingChange: (isProcessing: boolean) => void;
}

// Map an app language code (e.g. "en", "ar") to a Web Speech locale.
const SPEECH_LOCALES: Record<string, string> = {
  en: 'en-US', ar: 'ar-SA', hi: 'hi-IN', ur: 'ur-PK', bn: 'bn-BD',
  fr: 'fr-FR', de: 'de-DE', ja: 'ja-JP', zh: 'zh-CN'
};

// How long to wait before speech starts (user opens mic and thinks).
const INITIAL_SILENCE_TIMEOUT_MS = 12000;
// How long to wait after the user pauses mid-sentence.
const SPEECH_SILENCE_TIMEOUT_MS = 5500;
// Hard cap so recording cannot run indefinitely.
const MAX_RECORDING_MS = 90000;
// Volume threshold for detecting speech during MediaRecorder capture.
const SPEECH_VOLUME_THRESHOLD = 0.08;

const resolveSpeechLocale = (): string => {
  try {
    const raw = localStorage.getItem('voiceSettings');
    if (raw) {
      const parsed = JSON.parse(raw);
      const lang = parsed?.default_language;
      if (lang && SPEECH_LOCALES[lang]) return SPEECH_LOCALES[lang];
    }
  } catch {
    /* ignore malformed cache */
  }
  const i18nLang = (localStorage.getItem('i18nextLng') || '').slice(0, 2);
  return SPEECH_LOCALES[i18nLang] || 'en-US';
};

const GlobalVoiceInputHandler: React.FC<GlobalVoiceInputHandlerProps> = ({
  onTranscription,
  onProcessingChange
}) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const recognitionRef = useRef<SpeechRecognition | null>(null);
  const finalTranscriptRef = useRef<string>('');
  const isListeningRef = useRef(false);
  const stopRequestedRef = useRef(false);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const usingMediaRecorderRef = useRef(false);
  const startInProgressRef = useRef(false);
  const queuedStartAfterStopRef = useRef(false);
  const previewTranscribingRef = useRef(false);
  const previewTranscriptRef = useRef('');
  const previewIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Web Audio analysis for the live waveform
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const mediaStreamRef = useRef<MediaStream | null>(null);
  const rafRef = useRef<number | null>(null);

  // Silence detection state
  const silenceTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const maxRecordingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hasSpeechRef = useRef<boolean>(false);
  const recordingStartedAtRef = useRef<number>(0);

  const onTranscriptionRef = useRef(onTranscription);
  const onProcessingChangeRef = useRef(onProcessingChange);

  useEffect(() => {
    onTranscriptionRef.current = onTranscription;
    onProcessingChangeRef.current = onProcessingChange;
  }, [onTranscription, onProcessingChange]);

  const clearSilenceTimeout = useCallback(() => {
    if (silenceTimeoutRef.current) {
      clearTimeout(silenceTimeoutRef.current);
      silenceTimeoutRef.current = null;
    }
  }, []);

  const clearRecordingTimers = useCallback(() => {
    clearSilenceTimeout();
    if (maxRecordingTimeoutRef.current) {
      clearTimeout(maxRecordingTimeoutRef.current);
      maxRecordingTimeoutRef.current = null;
    }
  }, [clearSilenceTimeout]);

  const clearPreviewTranscription = useCallback(() => {
    if (previewIntervalRef.current) {
      clearInterval(previewIntervalRef.current);
      previewIntervalRef.current = null;
    }
    previewTranscribingRef.current = false;
    previewTranscriptRef.current = '';
  }, []);

  const getSilenceTimeoutMs = useCallback(() => (
    hasSpeechRef.current ? SPEECH_SILENCE_TIMEOUT_MS : INITIAL_SILENCE_TIMEOUT_MS
  ), []);

  const releaseMicrophoneStream = useCallback(() => {
    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach((track) => track.stop());
      mediaStreamRef.current = null;
    }
  }, []);

  const ensureMicrophoneStream = useCallback(async (): Promise<MediaStream | null> => {
    const stream = await warmUpMicrophone(mediaStreamRef.current);
    if (stream) {
      mediaStreamRef.current = stream;
    }
    return stream;
  }, []);

  const stopWaveform = useCallback(() => {
    if (rafRef.current !== null) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    if (audioContextRef.current) {
      audioContextRef.current.close().catch(() => {});
      audioContextRef.current = null;
    }
    analyserRef.current = null;
    window.dispatchEvent(new CustomEvent('voiceWaveform', { detail: { levels: [], volume: 0 } }));
  }, []);

  const startWaveform = useCallback(async (existingStream?: MediaStream | null) => {
    try {
      if (audioContextRef.current) return;

      const stream = existingStream || mediaStreamRef.current || await ensureMicrophoneStream();
      if (!stream) return;

      const AudioCtx = (window as any).AudioContext || (window as any).webkitAudioContext;
      const audioContext: AudioContext = new AudioCtx();
      if (audioContext.state === 'suspended') {
        await audioContext.resume();
      }
      const source = audioContext.createMediaStreamSource(stream);
      const analyser = audioContext.createAnalyser();
      analyser.fftSize = 256;
      analyser.smoothingTimeConstant = 0.8;
      source.connect(analyser);

      audioContextRef.current = audioContext;
      analyserRef.current = analyser;

      const bufferLength = analyser.frequencyBinCount;
      const dataArray = new Uint8Array(bufferLength);
      const BAR_COUNT = 28;

      const tick = () => {
        if (!analyserRef.current) return;
        analyserRef.current.getByteFrequencyData(dataArray);

        const levels: number[] = new Array(BAR_COUNT);
        const step = Math.floor(bufferLength / BAR_COUNT) || 1;
        let sum = 0;
        for (let i = 0; i < BAR_COUNT; i++) {
          const v = dataArray[i * step] / 255;
          levels[i] = v;
          sum += v;
        }
        const volume = sum / BAR_COUNT;

        if (
          usingMediaRecorderRef.current
          && volume > SPEECH_VOLUME_THRESHOLD
          && isListeningRef.current
        ) {
          if (!hasSpeechRef.current) {
            hasSpeechRef.current = true;
          }
          resetSilenceDetectionRef.current();
        }

        window.dispatchEvent(new CustomEvent('voiceWaveform', { detail: { levels, volume } }));
        rafRef.current = requestAnimationFrame(tick);
      };
      tick();
    } catch (err) {
      console.warn('Waveform analysis unavailable:', err);
    }
  }, [ensureMicrophoneStream]);

  const stopRecognition = useCallback(() => {
    startInProgressRef.current = false;
    stopRequestedRef.current = true;
    clearRecordingTimers();
    clearPreviewTranscription();

    if (usingMediaRecorderRef.current && mediaRecorderRef.current) {
      if (mediaRecorderRef.current.state !== 'inactive') {
        try {
          mediaRecorderRef.current.stop();
        } catch {
          /* already stopped */
        }
      }
      return;
    }

    if (recognitionRef.current) {
      try {
        recognitionRef.current.stop();
      } catch {
        /* already stopped */
      }
    }
  }, [clearPreviewTranscription, clearRecordingTimers]);

  const scheduleMaxRecordingTimeout = useCallback(() => {
    if (maxRecordingTimeoutRef.current || !recordingStartedAtRef.current) return;
    const elapsed = Date.now() - recordingStartedAtRef.current;
    const remaining = Math.max(MAX_RECORDING_MS - elapsed, 1000);
    maxRecordingTimeoutRef.current = setTimeout(() => {
      if (!isListeningRef.current) return;
      console.log('⏰ Max recording duration reached, stopping recording');
      stopRecognition();
    }, remaining);
  }, [stopRecognition]);

  const startSilenceDetection = useCallback(() => {
    clearSilenceTimeout();

    silenceTimeoutRef.current = setTimeout(() => {
      if (!isListeningRef.current) return;
      console.log(
        hasSpeechRef.current
          ? '🔇 Pause detected after speech, stopping recording'
          : '🔇 No speech detected within listening window, stopping recording'
      );
      window.dispatchEvent(new CustomEvent('silenceDetected'));
      stopRecognition();
    }, getSilenceTimeoutMs());
  }, [clearSilenceTimeout, getSilenceTimeoutMs, stopRecognition]);

  const resetSilenceDetectionRef = useRef<() => void>(() => {});

  const resetSilenceDetection = useCallback(() => {
    if (!isListeningRef.current) return;
    startSilenceDetection();
  }, [startSilenceDetection]);

  useEffect(() => {
    resetSilenceDetectionRef.current = resetSilenceDetection;
  }, [resetSilenceDetection]);

  const finishMediaRecorderCapture = useCallback(async () => {
    startInProgressRef.current = false;
    usingMediaRecorderRef.current = false;
    isListeningRef.current = false;
    recordingStartedAtRef.current = 0;
    stopWaveform();
    clearRecordingTimers();
    clearPreviewTranscription();
    window.dispatchEvent(new CustomEvent('recordingStateChange', { detail: false }));
    if (queuedStartAfterStopRef.current) {
      queuedStartAfterStopRef.current = false;
      setTimeout(() => window.dispatchEvent(new CustomEvent('startVoiceInput')), 80);
    }

    const chunks = audioChunksRef.current;
    audioChunksRef.current = [];
    mediaRecorderRef.current = null;
    releaseMicrophoneStream();

    if (!chunks.length) {
      onProcessingChangeRef.current(false);
      return;
    }

    const mimeType = chunks[0]?.type || 'audio/webm';
    const blob = new Blob(chunks, { type: mimeType });

    let webSpeechFallbackStarted = false;

    const tryWebSpeechFallback = (): boolean => {
      if (!supportsWebSpeechRecognition() || !recognitionRef.current) return false;
      try {
        hasSpeechRef.current = false;
        finalTranscriptRef.current = '';
        stopRequestedRef.current = false;
        recognitionRef.current.start();
        return true;
      } catch (error) {
        console.warn('Web Speech fallback failed:', error);
        return false;
      }
    };

    try {
      setIsProcessing(true);
      onProcessingChangeRef.current(true);
      const text = await transcribeAudioBlob(blob);
      if (text.length > 1) {
        onTranscriptionRef.current(text);
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Transcription failed';
      console.warn('Media recorder transcription failed:', error);

      // If interim preview already captured speech, continue with that text
      // instead of dropping the command on final transcription failure.
      const previewText = previewTranscriptRef.current.trim();
      if (previewText.length > 1) {
        onTranscriptionRef.current(previewText);
        return;
      }

      if (isTranscriptionUnavailableError(message) && tryWebSpeechFallback()) {
        webSpeechFallbackStarted = true;
        return;
      }

      window.dispatchEvent(new CustomEvent('voiceMicError', {
        detail: {
          message: isTranscriptionUnavailableError(message)
            ? 'Voice transcription is temporarily unavailable. Please type your message or try again shortly.'
            : message,
        },
      }));
    } finally {
      if (!webSpeechFallbackStarted) {
        setIsProcessing(false);
        onProcessingChangeRef.current(false);
      }
    }
  }, [clearPreviewTranscription, clearRecordingTimers, releaseMicrophoneStream, stopWaveform]);

  const startMediaRecorderCapture = useCallback(async (stream: MediaStream) => {
    audioChunksRef.current = [];
    usingMediaRecorderRef.current = true;
    stopRequestedRef.current = false;
    recordingStartedAtRef.current = Date.now();
    hasSpeechRef.current = false;
    finalTranscriptRef.current = '';
    previewTranscriptRef.current = '';

    const recorder = createMediaRecorder(stream, (chunk) => {
      audioChunksRef.current.push(chunk);
    });

    recorder.addEventListener('stop', () => {
      void finishMediaRecorderCapture();
    });

    mediaRecorderRef.current = recorder;
    recorder.start(250);
    isListeningRef.current = true;
    startInProgressRef.current = false;

    // Provide near-real-time transcript updates on iOS MediaRecorder fallback only.
    previewIntervalRef.current = setInterval(async () => {
      if (!isListeningRef.current || previewTranscribingRef.current) return;
      if (audioChunksRef.current.length < 6) return;

      previewTranscribingRef.current = true;
      try {
        const previewBlob = new Blob(audioChunksRef.current, {
          type: audioChunksRef.current[0]?.type || 'audio/webm',
        });
        const previewText = (await transcribeAudioBlob(previewBlob)).trim();
        if (previewText.length > 1 && previewText !== previewTranscriptRef.current) {
          previewTranscriptRef.current = previewText;
          hasSpeechRef.current = true;
          resetSilenceDetectionRef.current();
          window.dispatchEvent(new CustomEvent('transcriptUpdate', {
            detail: { transcript: previewText, isFinal: false },
          }));
        }
      } catch {
        // Ignore interim transcription errors/no-speech and keep listening.
      } finally {
        previewTranscribingRef.current = false;
      }
    }, 5000);

    await startWaveform(stream);
    startSilenceDetection();
    scheduleMaxRecordingTimeout();
    window.dispatchEvent(new CustomEvent('recordingStateChange', { detail: true }));
  }, [finishMediaRecorderCapture, scheduleMaxRecordingTimeout, startSilenceDetection, startWaveform]);

  const createRecognitionSession = useCallback(() => {
    if (!supportsWebSpeechRecognition()) return null;

    if (recognitionRef.current) {
      try {
        recognitionRef.current.abort();
      } catch {
        try {
          recognitionRef.current.stop();
        } catch {
          /* already stopped */
        }
      }
      recognitionRef.current = null;
    }

    const SpeechRecognitionCtor = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    const recognition = new SpeechRecognitionCtor() as SpeechRecognition;

    recognition.continuous = !isIOS();
    recognition.interimResults = true;
    recognition.lang = resolveSpeechLocale();

    recognition.onstart = () => {
      console.log('🎤 Global voice input started');
      startInProgressRef.current = false;
      isListeningRef.current = true;
      stopRequestedRef.current = false;
      if (!recordingStartedAtRef.current) {
        recordingStartedAtRef.current = Date.now();
      }
      hasSpeechRef.current = false;
      finalTranscriptRef.current = '';

      void startWaveform(mediaStreamRef.current);
      startSilenceDetection();
      scheduleMaxRecordingTimeout();
      window.dispatchEvent(new CustomEvent('recordingStateChange', { detail: true }));
    };

    recognition.onresult = (event: SpeechRecognitionEvent) => {
      let finalTranscript = '';
      let interimTranscript = '';

      for (let i = event.resultIndex; i < event.results.length; i++) {
        const transcript = event.results[i][0].transcript;
        if (event.results[i].isFinal) {
          finalTranscript += transcript;
        } else {
          interimTranscript += transcript;
        }
      }

      if (finalTranscript.trim() || interimTranscript.trim()) {
        hasSpeechRef.current = true;
        resetSilenceDetection();
      } else if (hasSpeechRef.current) {
        resetSilenceDetection();
      }

      if (finalTranscript) {
        finalTranscriptRef.current = `${finalTranscriptRef.current} ${finalTranscript}`.replace(/\s+/g, ' ').trim();
        console.log('📝 Global voice input final transcript:', finalTranscriptRef.current);
      }

      const currentTranscript = `${finalTranscriptRef.current} ${interimTranscript}`.replace(/\s+/g, ' ').trim();
      if (currentTranscript) {
        window.dispatchEvent(new CustomEvent('transcriptUpdate', {
          detail: {
            transcript: currentTranscript,
            isFinal: finalTranscript.length > 0
          }
        }));
      }
    };

    recognition.onend = () => {
      const elapsed = recordingStartedAtRef.current
        ? Date.now() - recordingStartedAtRef.current
        : 0;
      const shouldRetryForSpeech = !isMobileDevice()
        && !stopRequestedRef.current
        && !hasSpeechRef.current
        && !finalTranscriptRef.current.trim()
        && elapsed > 0
        && elapsed < INITIAL_SILENCE_TIMEOUT_MS;

      if (shouldRetryForSpeech && recognitionRef.current) {
        console.log('🔄 Restarting recognition — still waiting for you to speak');
        try {
          recognitionRef.current.start();
          resetSilenceDetection();
          return;
        } catch (error) {
          console.warn('Could not restart recognition while waiting for speech:', error);
        }
      }

      console.log('🎤 Voice input ended');
      startInProgressRef.current = false;
      isListeningRef.current = false;
      recordingStartedAtRef.current = 0;
      stopWaveform();
      releaseMicrophoneStream();
      clearRecordingTimers();
      clearPreviewTranscription();
      onProcessingChangeRef.current(false);
      window.dispatchEvent(new CustomEvent('recordingStateChange', { detail: false }));

      const shouldAutoRestart = queuedStartAfterStopRef.current;
      if (shouldAutoRestart) {
        queuedStartAfterStopRef.current = false;
        setTimeout(() => window.dispatchEvent(new CustomEvent('startVoiceInput')), 80);
      }

      const currentTranscript = finalTranscriptRef.current.trim();
      finalTranscriptRef.current = '';

      if (currentTranscript && currentTranscript.length > 1) {
        console.log('🎤 Processing voice input:', currentTranscript);
        setIsProcessing(true);
        onProcessingChangeRef.current(true);
        onTranscriptionRef.current(currentTranscript);
      } else if (!shouldAutoRestart) {
        console.log('🎤 No speech detected, ignoring');
        setIsProcessing(false);
        onProcessingChangeRef.current(false);
      }
    };

    recognition.onerror = (event: SpeechRecognitionErrorEvent) => {
      if (event.error === 'no-speech' && !hasSpeechRef.current && !stopRequestedRef.current) {
        console.log('🔇 Browser reported no speech yet — keeping the mic open');
        resetSilenceDetection();
        return;
      }

      console.warn('⚠️ Global voice input error:', event.error);
      startInProgressRef.current = false;
      isListeningRef.current = false;
      recordingStartedAtRef.current = 0;
      stopWaveform();
      releaseMicrophoneStream();
      clearRecordingTimers();
      clearPreviewTranscription();
      onProcessingChangeRef.current(false);
      window.dispatchEvent(new CustomEvent('recordingStateChange', { detail: false }));

      if (queuedStartAfterStopRef.current) {
        queuedStartAfterStopRef.current = false;
        setTimeout(() => window.dispatchEvent(new CustomEvent('startVoiceInput')), 120);
      }
    };

    recognitionRef.current = recognition;
    return recognition;
  }, [clearPreviewTranscription, clearRecordingTimers, releaseMicrophoneStream, resetSilenceDetection, scheduleMaxRecordingTimeout, startSilenceDetection, startWaveform, stopWaveform]);

  useEffect(() => () => {
    stopRecognition();
    recognitionRef.current = null;
    clearRecordingTimers();
    clearPreviewTranscription();
    stopWaveform();
    releaseMicrophoneStream();
  }, [clearPreviewTranscription, clearRecordingTimers, releaseMicrophoneStream, stopRecognition, stopWaveform]);

  // Listen for global voice input events
  useEffect(() => {
    const handleStartVoiceInput = async (event: Event) => {
      console.log('🎤 Start voice input event received');
      if (isListeningRef.current || startInProgressRef.current) {
        queuedStartAfterStopRef.current = true;
        if (isListeningRef.current) {
          stopRecognition();
        }
        return;
      }
      startInProgressRef.current = true;

      recordingStartedAtRef.current = 0;
      hasSpeechRef.current = false;
      finalTranscriptRef.current = '';
      stopRequestedRef.current = false;
      window.dispatchEvent(new CustomEvent('transcriptUpdate', {
        detail: { transcript: '', isFinal: false },
      }));

      const prewarmed = (event as CustomEvent<{ stream?: MediaStream | null }>).detail?.stream;
      const stream = prewarmed?.active ? prewarmed : await ensureMicrophoneStream();
      if (stream) {
        mediaStreamRef.current = stream;
      }
      if (!stream) {
        startInProgressRef.current = false;
        window.dispatchEvent(new CustomEvent('recordingStateChange', { detail: false }));
        window.dispatchEvent(new CustomEvent('voiceMicError', {
          detail: { message: 'Microphone access denied. Please allow microphone access in your browser settings.' },
        }));
        return;
      }

      if (shouldUseMediaRecorderFallback()) {
        try {
          await startMediaRecorderCapture(stream);
        } catch (error) {
          console.warn('Could not start media recorder capture:', error);
          startInProgressRef.current = false;
          window.dispatchEvent(new CustomEvent('recordingStateChange', { detail: false }));
          releaseMicrophoneStream();
        }
        return;
      }

      const recognition = createRecognitionSession();
      if (!recognition) {
        startInProgressRef.current = false;
        window.dispatchEvent(new CustomEvent('recordingStateChange', { detail: false }));
        window.dispatchEvent(new CustomEvent('voiceMicError', {
          detail: { message: 'Speech recognition is not supported on this device.' },
        }));
        releaseMicrophoneStream();
        return;
      }

      try {
        recognition.start();
      } catch (error) {
        console.warn('Could not start recognition (may already be active):', error);
        const retryRecognition = createRecognitionSession();
        if (!retryRecognition) {
          startInProgressRef.current = false;
          window.dispatchEvent(new CustomEvent('recordingStateChange', { detail: false }));
          releaseMicrophoneStream();
          return;
        }

        setTimeout(() => {
          if (!recognitionRef.current || isListeningRef.current || stopRequestedRef.current) {
            startInProgressRef.current = false;
            return;
          }
          try {
            recognitionRef.current.start();
          } catch (retryError) {
            console.warn('Recognition retry failed:', retryError);
            startInProgressRef.current = false;
            window.dispatchEvent(new CustomEvent('recordingStateChange', { detail: false }));
            releaseMicrophoneStream();
          }
        }, 120);
      }
    };

    const handleStopVoiceInput = () => {
      console.log('🎤 Stop voice input event received');
      queuedStartAfterStopRef.current = false;
      stopRecognition();
    };

    window.addEventListener('startVoiceInput', handleStartVoiceInput);
    window.addEventListener('stopVoiceInput', handleStopVoiceInput);

    return () => {
      window.removeEventListener('startVoiceInput', handleStartVoiceInput);
      window.removeEventListener('stopVoiceInput', handleStopVoiceInput);
    };
  }, [createRecognitionSession, ensureMicrophoneStream, releaseMicrophoneStream, startMediaRecorderCapture, stopRecognition]);

  return null;
};

export default GlobalVoiceInputHandler;
